import { log, logE } from "../../../shared/services/debug-logger";
import { api, apiUrl } from "../../../shared/services/service";
import { VESSEL_CARD_ZN_MOCK_FACTORS, VESSEL_CARD_ZN_UNAVAILABLE_REASONS } from "../constants/vessel-card-zn.constants";
import { VESSEL_CARD_DEMO_ZN_FACTORS } from "../mocks/vessel-card.mocks";
import type { VesselCardZnRequest, VesselCardZnResponse, VesselCardZnScenario } from "../models/vessel-card-zn.models";
import { getVesselCardZnSegment } from "../utils/vessel-card-zn.utils";

// Reads existing stored ZN points through the authenticated backend instead of requesting vendor predictions.
// The API adapter is parked on the backend because production already contains the comparison points.
// Reject other sources here so neither the parked API nor synthetic fixtures become a live fallback.
export const getVesselCardZnModel = async (imo: string, signal: AbortSignal): Promise<VesselCardZnResponse> => {
  log("REPO - getVesselCardZnModel - start", { imo });
  try {
    const response = await api.get<VesselCardZnResponse>(`${apiUrl}/pbi/vessel-card/zn-model`, {
      params: { imo },
      signal,
    });
    if (
      response.data.imo !== imo ||
      response.data.source !== "snowflake" ||
      !Array.isArray(response.data.predictions)
    ) {
      log("REPO - getVesselCardZnModel - invalid response");
      throw new Error("Invalid ZN model response.");
    }
    const reason =
      response.data.reason === null
        ? null
        : response.data.reason === "partial_predictions" ||
            Object.prototype.hasOwnProperty.call(VESSEL_CARD_ZN_UNAVAILABLE_REASONS, response.data.reason)
          ? response.data.reason
          : "unknown";
    log("REPO - getVesselCardZnModel - response", { modelAvailable: response.data.modelAvailable, reason });

    return response.data;
  } catch (error) {
    if (!signal.aborted) logE(error, { operation: "REPO - getVesselCardZnModel - failed" });
    throw error;
  }
};

// Explicit fixtures only. Dates are relative and predictions remain synthetic, never a live fallback.
export const getVesselCardZnMockModel = async (
  request: VesselCardZnRequest,
  scenario: VesselCardZnScenario,
  signal: AbortSignal,
): Promise<VesselCardZnResponse> => {
  if (signal.aborted) throw new DOMException("Request aborted", "AbortError");

  const trainedAt = new Date();
  trainedAt.setUTCDate(trainedAt.getUTCDate() - (scenario === "stale" ? 120 : 14));
  const factors = VESSEL_CARD_DEMO_ZN_FACTORS[request.imo] ?? VESSEL_CARD_ZN_MOCK_FACTORS;
  const segment = getVesselCardZnSegment(request.summerDwt);
  const trainedDate =
    scenario === "unavailable" || scenario === "missingTraining" ? null : trainedAt.toISOString().slice(0, 10);

  return {
    imo: request.imo,
    source: "mock",
    modelAvailable: scenario !== "unavailable" && segment !== null,
    trainedAt: trainedDate,
    trainingSource: trainedDate ? "Synthetic fixture" : null,
    modelVersion: "mock",
    evaluatedAt: new Date().toISOString(),
    segment: segment?.name ?? null,
    ballastDraft: segment?.ballastDraft ?? null,
    ladenDraft: segment?.ladenDraft ?? null,
    reason:
      scenario === "missingTraining"
        ? "training_date_unverified"
        : scenario === "stale"
          ? "model_stale"
          : !segment
            ? "draft_reference_unavailable"
            : scenario === "unavailable"
              ? "model_not_enabled"
              : null,
    predictions:
      scenario === "unavailable" || scenario === "missingTraining" || !segment
        ? []
        : request.points.map((point) => ({
            mode: point.mode,
            speed: point.speed,
            consumption:
              scenario === "partial" && point.mode === "Laden FULL"
                ? null
                : point.expectedConsumption !== null &&
                    Number.isFinite(point.expectedConsumption) &&
                    point.expectedConsumption >= 0 &&
                    factors[point.mode] !== undefined
                  ? point.expectedConsumption * factors[point.mode]
                  : null,
          })),
  };
};
